import { ConsoleLogger } from "./ILogger";
import { MmdDataDeserializer } from "./mmdDataDeserializer";
import { PmxObject } from "./pmxObject";
class IndexReader {
    _vertexIndexSize;
    _textureIndexSize;
    _materialIndexSize;
    _boneIndexSize;
    _morphIndexSize;
    _rigidBodyIndexSize;
    constructor(vertexIndexSize, textureIndexSize, materialIndexSize, boneIndexSize, morphIndexSize, rigidBodyIndexSize) {
        this._vertexIndexSize = vertexIndexSize;
        this._textureIndexSize = textureIndexSize;
        this._materialIndexSize = materialIndexSize;
        this._boneIndexSize = boneIndexSize;
        this._morphIndexSize = morphIndexSize;
        this._rigidBodyIndexSize = rigidBodyIndexSize;
    }
    getVertexIndex(dataDeserializer) {
        switch (this._vertexIndexSize) {
            case 1:
                return dataDeserializer.getUint8();
            case 2:
                return dataDeserializer.getUint16();
            case 4:
                return dataDeserializer.getInt32();
            default:
                throw new Error(`Invalid vertexIndexSize: ${this._vertexIndexSize}`);
        }
    }
    _getNonVertexIndex(dataDeserializer, indexSize) {
        switch (indexSize) {
            case 1:
                return dataDeserializer.getInt8();
            case 2:
                return dataDeserializer.getInt16();
            case 4:
                return dataDeserializer.getInt32();
            default:
                throw new Error(`Invalid indexSize: ${indexSize}`);
        }
    }
    getTextureIndex(dataDeserializer) {
        return this._getNonVertexIndex(dataDeserializer, this._textureIndexSize);
    }
    getMaterialIndex(dataDeserializer) {
        return this._getNonVertexIndex(dataDeserializer, this._materialIndexSize);
    }
    getBoneIndex(dataDeserializer) {
        return this._getNonVertexIndex(dataDeserializer, this._boneIndexSize);
    }
    getMorphIndex(dataDeserializer) {
        return this._getNonVertexIndex(dataDeserializer, this._morphIndexSize);
    }
    getRigidBodyIndex(dataDeserializer) {
        return this._getNonVertexIndex(dataDeserializer, this._rigidBodyIndexSize);
    }
}
/**
 * PmxReader is a static class that parses PMX data
 */
export class PmxReader {
    constructor() { }
    /**
     * Parses PMX data asynchronously
     * @param data Arraybuffer of PMX data
     * @param logger Logger
     * @returns PMX data
     * @throws {Error} If the parse fails
     */
    static async ParseAsync(data, logger = new ConsoleLogger()) {
        const dataDeserializer = new MmdDataDeserializer(data);
        const header = this._ParseHeader(dataDeserializer, logger);
        const indexReader = new IndexReader(header.vertexIndexSize, header.textureIndexSize, header.materialIndexSize, header.boneIndexSize, header.morphIndexSize, header.rigidBodyIndexSize);
        const vertices = await this._ParseVerticesAsync(dataDeserializer, indexReader, header);
        const faces = this._ParseFaces(dataDeserializer, indexReader, header);
        const textures = this._ParseTextures(dataDeserializer);
        const materials = this._ParseMaterials(dataDeserializer, indexReader);
        const bones = this._ParseBones(dataDeserializer, indexReader);
        const morphs = this._ParseMorphs(dataDeserializer, indexReader);
        const displayFrames = this._ParseDisplayFrames(dataDeserializer, indexReader);
        const rigidBodies = this._ParseRigidBodies(dataDeserializer, indexReader);
        const joints = this._ParseJoints(dataDeserializer, indexReader);
        const softBodies = header.version <= 2.0
            ? []
            : this._ParseSoftBodies(dataDeserializer, indexReader, header);
        if (dataDeserializer.bytesAvailable > 0) {
            logger.warn(`There are ${dataDeserializer.bytesAvailable} bytes left after parsing`);
        }
        const pmxObject = {
            header,
            vertices,
            faces,
            textures,
            materials,
            bones,
            morphs,
            displayFrames,
            rigidBodies,
            joints,
            softBodies
        };
        return pmxObject;
    }
    static _ParseHeader(dataDeserializer, logger) {
        if (dataDeserializer.bytesAvailable < (4 // signature
            + 4 // version (float32)
            + 1 // globalsCount (uint8)
            + 1 // encoding (uint8)
            + 1 // additionalVec4Count (uint8)
            + 1 // vertexIndexSize (uint8)
            + 1 // textureIndexSize (uint8)
            + 1 // materialIndexSize (uint8)
            + 1 // boneIndexSize (uint8)
            + 1 // morphIndexSize (uint8)
            + 1 // rigidBodyIndexSize (uint8)
        )) {
            throw new RangeError("is not pmx file");
        }
        const signature = dataDeserializer.getSignatureString(3);
        if (signature !== "PMX") {
            throw new RangeError("is not pmx file");
        }
        dataDeserializer.getInt8(); // skip byte alignment
        const version = dataDeserializer.getFloat32();
        const globalsCount = dataDeserializer.getUint8();
        const encoding = dataDeserializer.getUint8();
        dataDeserializer.initializeTextDecoder(encoding === PmxObject.Header.Encoding.Utf8 ? "utf-8" : "utf-16le");
        const additionalVec4Count = dataDeserializer.getUint8();
        const vertexIndexSize = dataDeserializer.getUint8();
        const textureIndexSize = dataDeserializer.getUint8();
        const materialIndexSize = dataDeserializer.getUint8();
        const boneIndexSize = dataDeserializer.getUint8();
        const morphIndexSize = dataDeserializer.getUint8();
        const rigidBodyIndexSize = dataDeserializer.getUint8();
        if (globalsCount < 8) {
            throw new Error(`Invalid globalsCount: ${globalsCount}`);
        }
        else if (8 < globalsCount) {
            logger.warn(`globalsCount is greater than 8: ${globalsCount} files may be corrupted or higher version`);
            for (let i = 8; i < globalsCount; ++i) {
                dataDeserializer.getUint8();
            }
        }
        const modelName = dataDeserializer.getDecoderString(dataDeserializer.getInt32(), false);
        const englishModelName = dataDeserializer.getDecoderString(dataDeserializer.getInt32(), false);
        const comment = dataDeserializer.getDecoderString(dataDeserializer.getInt32(), false);
        const englishComment = dataDeserializer.getDecoderString(dataDeserializer.getInt32(), false);
        const header = {
            signature,
            version,
            encoding,
            additionalVec4Count,
            vertexIndexSize,
            textureIndexSize,
            materialIndexSize,
            boneIndexSize,
            morphIndexSize,
            rigidBodyIndexSize,
            modelName,
            englishModelName,
            comment,
            englishComment
        };
        return header;
    }
    static async _ParseVerticesAsync(dataDeserializer, indexReader, header) {
        const verticesCount = dataDeserializer.getInt32();
        const vertices = [];
        let time = performance.now();
        for (let i = 0; i < verticesCount; ++i) {
            const position = dataDeserializer.getFloat32Tuple(3);
            const normal = dataDeserializer.getFloat32Tuple(3);
            const uv = dataDeserializer.getFloat32Tuple(2);
            const additionalVec4 = [];
            for (let j = 0; j < header.additionalVec4Count; ++j) {
                additionalVec4.push(dataDeserializer.getFloat32Tuple(4));
            }
            const weightType = dataDeserializer.getUint8();
            let boneWeight;
            switch (weightType) {
                case PmxObject.Vertex.BoneWeightType.Bdef1: {
                    const bdef1weight = {
                        boneIndices: indexReader.getBoneIndex(dataDeserializer),
                        boneWeights: null
                    };
                    boneWeight = bdef1weight;
                    break;
                }
                case PmxObject.Vertex.BoneWeightType.Bdef2: {
                    const bdef2weight = {
                        boneIndices: [indexReader.getBoneIndex(dataDeserializer), indexReader.getBoneIndex(dataDeserializer)],
                        boneWeights: dataDeserializer.getFloat32()
                    };
                    boneWeight = bdef2weight;
                    break;
                }
                case PmxObject.Vertex.BoneWeightType.Bdef4: {
                    const bdef4weight = {
                        boneIndices: [
                            indexReader.getBoneIndex(dataDeserializer),
                            indexReader.getBoneIndex(dataDeserializer),
                            indexReader.getBoneIndex(dataDeserializer),
                            indexReader.getBoneIndex(dataDeserializer)
                        ],
                        boneWeights: [
                            dataDeserializer.getFloat32(),
                            dataDeserializer.getFloat32(),
                            dataDeserializer.getFloat32(),
                            dataDeserializer.getFloat32()
                        ]
                    };
                    boneWeight = bdef4weight;
                    break;
                }
                case PmxObject.Vertex.BoneWeightType.Sdef: {
                    const sdefweight = {
                        boneIndices: [indexReader.getBoneIndex(dataDeserializer), indexReader.getBoneIndex(dataDeserializer)],
                        boneWeights: {
                            boneWeight0: dataDeserializer.getFloat32(),
                            c: dataDeserializer.getFloat32Tuple(3),
                            r0: dataDeserializer.getFloat32Tuple(3),
                            r1: dataDeserializer.getFloat32Tuple(3)
                        }
                    };
                    boneWeight = sdefweight;
                    break;
                }
                case PmxObject.Vertex.BoneWeightType.Qdef: {
                    const qdefweight = {
                        boneIndices: [
                            indexReader.getBoneIndex(dataDeserializer),
                            indexReader.getBoneIndex(dataDeserializer),
                            indexReader.getBoneIndex(dataDeserializer),
                            indexReader.getBoneIndex(dataDeserializer)
                        ],
                        boneWeights: [
                            dataDeserializer.getFloat32(),
                            dataDeserializer.getFloat32(),
                            dataDeserializer.getFloat32(),
                            dataDeserializer.getFloat32()
                        ]
                    };
                    boneWeight = qdefweight;
                    break;
                }
                default:
                    throw new Error(`Invalid weightType: ${weightType}`);
            }
            const edgeScale = dataDeserializer.getFloat32();
            vertices.push({
                position,
                normal,
                uv,
                additionalVec4,
                weightType,
                boneWeight,
                edgeScale
            });
            if (i % 10000 === 0 && 100 < performance.now() - time) {
                await new Promise(resolve => setTimeout(resolve, 0));
                time = performance.now();
            }
        }
        return vertices;
    }
    static _ParseFaces(dataDeserializer, indexReader, header) {
        const facesindicesCount = dataDeserializer.getInt32();
        // const faces: PmxObject.Face[] = [];
        // for (let i = 0; i < facesIndiceCount; i += 3) {
        //     faces.push([getVertexIndex(), getVertexIndex(), getVertexIndex()]);
        // }
        const faceArrayBuffer = new ArrayBuffer(facesindicesCount * header.vertexIndexSize);
        let faces;
        switch (header.vertexIndexSize) {
            case 1:
                faces = new Uint8Array(faceArrayBuffer);
                break;
            case 2:
                faces = new Uint16Array(faceArrayBuffer);
                break;
            case 4:
                faces = new Int32Array(faceArrayBuffer);
                break;
            default:
                throw new Error(`Invalid vertexIndexSize: ${header.vertexIndexSize}`);
        }
        for (let i = 0; i < facesindicesCount; ++i) {
            faces[i] = indexReader.getVertexIndex(dataDeserializer);
        }
        return faces;
    }
    static _ParseTextures(dataDeserializer) {
        const texturesCount = dataDeserializer.getInt32();
        const textures = [];
        for (let i = 0; i < texturesCount; ++i) {
            const textureName = dataDeserializer.getDecoderString(dataDeserializer.getInt32(), false);
            textures.push(textureName);
        }
        return textures;
    }
    static _ParseMaterials(dataDeserializer, indexReader) {
        const materialsCount = dataDeserializer.getInt32();
        const materials = [];
        for (let i = 0; i < materialsCount; ++i) {
            const name = dataDeserializer.getDecoderString(dataDeserializer.getInt32(), false);
            const englishName = dataDeserializer.getDecoderString(dataDeserializer.getInt32(), false);
            const diffuse = dataDeserializer.getFloat32Tuple(4);
            const specular = dataDeserializer.getFloat32Tuple(3);
            const shininess = dataDeserializer.getFloat32();
            const ambient = dataDeserializer.getFloat32Tuple(3);
            const flag = dataDeserializer.getUint8();
            const edgeColor = dataDeserializer.getFloat32Tuple(4);
            const edgeSize = dataDeserializer.getFloat32();
            const textureIndex = indexReader.getTextureIndex(dataDeserializer);
            const sphereTextureIndex = indexReader.getTextureIndex(dataDeserializer);
            const sphereTextureMode = dataDeserializer.getUint8();
            const isSharedToonTexture = dataDeserializer.getUint8() === 1;
            const toonTextureIndex = isSharedToonTexture ? dataDeserializer.getUint8() : indexReader.getTextureIndex(dataDeserializer);
            const comment = dataDeserializer.getDecoderString(dataDeserializer.getInt32(), false);
            const surfaceCount = dataDeserializer.getInt32();
            const material = {
                name,
                englishName,
                diffuse,
                specular,
                shininess,
                ambient,
                flag,
                edgeColor,
                edgeSize,
                textureIndex,
                sphereTextureIndex,
                sphereTextureMode,
                isSharedToonTexture,
                toonTextureIndex,
                comment,
                surfaceCount
            };
            materials.push(material);
        }
        return materials;
    }
    static _ParseBones(dataDeserializer, indexReader) {
        const bonesCount = dataDeserializer.getInt32();
        const bones = [];
        for (let i = 0; i < bonesCount; ++i) {
            const name = dataDeserializer.getDecoderString(dataDeserializer.getInt32(), false);
            const englishName = dataDeserializer.getDecoderString(dataDeserializer.getInt32(), false);
            const position = dataDeserializer.getFloat32Tuple(3);
            const parentBoneIndex = indexReader.getBoneIndex(dataDeserializer);
            const transformOrder = dataDeserializer.getInt32();
            const flag = dataDeserializer.getUint16();
            let tailPosition;
            if (flag & PmxObject.Bone.Flag.UseBoneIndexAsTailPosition) {
                tailPosition = indexReader.getBoneIndex(dataDeserializer);
            }
            else {
                tailPosition = dataDeserializer.getFloat32Tuple(3);
            }
            let appendTransform;
            if (flag & PmxObject.Bone.Flag.HasAppendMove || flag & PmxObject.Bone.Flag.HasAppendRotate) {
                appendTransform = {
                    parentIndex: indexReader.getBoneIndex(dataDeserializer),
                    ratio: dataDeserializer.getFloat32()
                };
            }
            let axisLimit;
            if (flag & PmxObject.Bone.Flag.HasAxisLimit) {
                axisLimit = dataDeserializer.getFloat32Tuple(3);
            }
            let localVector;
            if (flag & PmxObject.Bone.Flag.HasLocalVector) {
                localVector = {
                    x: dataDeserializer.getFloat32Tuple(3),
                    z: dataDeserializer.getFloat32Tuple(3)
                };
            }
            let externalParentTransform;
            if (flag & PmxObject.Bone.Flag.IsExternalParentTransformed) {
                externalParentTransform = dataDeserializer.getInt32();
            }
            let ik;
            if (flag & PmxObject.Bone.Flag.IsIkEnabled) {
                const target = indexReader.getBoneIndex(dataDeserializer);
                const iteration = dataDeserializer.getInt32();
                const rotationConstraint = dataDeserializer.getFloat32();
                const links = [];
                const linksCount = dataDeserializer.getInt32();
                for (let i = 0; i < linksCount; ++i) {
                    const ikLinkTarget = indexReader.getBoneIndex(dataDeserializer);
                    const hasLimit = dataDeserializer.getUint8() === 1;
                    const link = {
                        target: ikLinkTarget,
                        limitation: hasLimit ? {
                            minimumAngle: dataDeserializer.getFloat32Tuple(3),
                            maximumAngle: dataDeserializer.getFloat32Tuple(3)
                        } : undefined
                    };
                    links.push(link);
                }
                ik = {
                    target,
                    iteration,
                    rotationConstraint,
                    links
                };
            }
            const bone = {
                name,
                englishName,
                position,
                parentBoneIndex,
                transformOrder,
                flag,
                tailPosition,
                appendTransform,
                axisLimit,
                localVector,
                externalParentTransform,
                ik
            };
            bones.push(bone);
        }
        return bones;
    }
    static _ParseMorphs(dataDeserializer, indexReader) {
        const morphsCount = dataDeserializer.getInt32();
        const morphs = [];
        for (let i = 0; i < morphsCount; ++i) {
            const name = dataDeserializer.getDecoderString(dataDeserializer.getInt32(), false);
            const englishName = dataDeserializer.getDecoderString(dataDeserializer.getInt32(), false);
            const category = dataDeserializer.getInt8();
            const type = dataDeserializer.getInt8();
            let morph = {
                name,
                englishName,
                category,
                type
            };
            const morphOffsetCount = dataDeserializer.getInt32();
            switch (type) {
                case PmxObject.Morph.Type.GroupMorph:
                    {
                        const indices = new Int32Array(morphOffsetCount);
                        const ratios = new Float32Array(morphOffsetCount);
                        for (let i = 0; i < morphOffsetCount; ++i) {
                            indices[i] = indexReader.getMorphIndex(dataDeserializer);
                            ratios[i] = dataDeserializer.getFloat32();
                        }
                        morph = {
                            ...morph,
                            indices,
                            ratios
                        };
                    }
                    break;
                case PmxObject.Morph.Type.VertexMorph:
                    {
                        const indices = new Int32Array(morphOffsetCount);
                        const positions = new Float32Array(morphOffsetCount * 3);
                        for (let i = 0; i < morphOffsetCount; ++i) {
                            indices[i] = indexReader.getVertexIndex(dataDeserializer);
                            positions[i * 3 + 0] = dataDeserializer.getFloat32();
                            positions[i * 3 + 1] = dataDeserializer.getFloat32();
                            positions[i * 3 + 2] = dataDeserializer.getFloat32();
                        }
                        morph = {
                            ...morph,
                            indices,
                            positions
                        };
                    }
                    break;
                case PmxObject.Morph.Type.BoneMorph:
                    {
                        const indices = new Int32Array(morphOffsetCount);
                        const positions = new Float32Array(morphOffsetCount * 3);
                        const rotations = new Float32Array(morphOffsetCount * 4);
                        for (let i = 0; i < morphOffsetCount; ++i) {
                            indices[i] = indexReader.getBoneIndex(dataDeserializer);
                            positions[i * 3 + 0] = dataDeserializer.getFloat32();
                            positions[i * 3 + 1] = dataDeserializer.getFloat32();
                            positions[i * 3 + 2] = dataDeserializer.getFloat32();
                            rotations[i * 4 + 0] = dataDeserializer.getFloat32();
                            rotations[i * 4 + 1] = dataDeserializer.getFloat32();
                            rotations[i * 4 + 2] = dataDeserializer.getFloat32();
                            rotations[i * 4 + 3] = dataDeserializer.getFloat32();
                        }
                        morph = {
                            ...morph,
                            indices,
                            positions,
                            rotations
                        };
                    }
                    break;
                case PmxObject.Morph.Type.UvMorph:
                case PmxObject.Morph.Type.AdditionalUvMorph1:
                case PmxObject.Morph.Type.AdditionalUvMorph2:
                case PmxObject.Morph.Type.AdditionalUvMorph3:
                case PmxObject.Morph.Type.AdditionalUvMorph4:
                    {
                        const indices = new Int32Array(morphOffsetCount);
                        const offsets = new Float32Array(morphOffsetCount * 4);
                        for (let i = 0; i < morphOffsetCount; ++i) {
                            indices[i] = indexReader.getVertexIndex(dataDeserializer);
                            offsets[i * 4 + 0] = dataDeserializer.getFloat32();
                            offsets[i * 4 + 1] = dataDeserializer.getFloat32();
                            offsets[i * 4 + 2] = dataDeserializer.getFloat32();
                            offsets[i * 4 + 3] = dataDeserializer.getFloat32();
                        }
                        morph = {
                            ...morph,
                            indices,
                            offsets
                        };
                    }
                    break;
                case PmxObject.Morph.Type.MaterialMorph:
                    {
                        const elements = [];
                        for (let i = 0; i < morphOffsetCount; ++i) {
                            const materialIndex = indexReader.getMaterialIndex(dataDeserializer);
                            const type = dataDeserializer.getUint8();
                            const diffuse = dataDeserializer.getFloat32Tuple(4);
                            const specular = dataDeserializer.getFloat32Tuple(3);
                            const shininess = dataDeserializer.getFloat32();
                            const ambient = dataDeserializer.getFloat32Tuple(3);
                            const edgeColor = dataDeserializer.getFloat32Tuple(4);
                            const edgeSize = dataDeserializer.getFloat32();
                            const textureColor = dataDeserializer.getFloat32Tuple(4);
                            const sphereTextureColor = dataDeserializer.getFloat32Tuple(4);
                            const toonTextureColor = dataDeserializer.getFloat32Tuple(4);
                            const element = {
                                index: materialIndex,
                                type,
                                diffuse,
                                specular,
                                shininess,
                                ambient,
                                edgeColor,
                                edgeSize,
                                textureColor,
                                sphereTextureColor,
                                toonTextureColor
                            };
                            elements.push(element);
                        }
                        morph = {
                            ...morph,
                            elements
                        };
                    }
                    break;
                case PmxObject.Morph.Type.FlipMorph:
                    {
                        const indices = new Int32Array(morphOffsetCount);
                        const ratios = new Float32Array(morphOffsetCount);
                        for (let i = 0; i < morphOffsetCount; ++i) {
                            indices[i] = indexReader.getMorphIndex(dataDeserializer);
                            ratios[i] = dataDeserializer.getFloat32();
                        }
                        morph = {
                            ...morph,
                            indices,
                            ratios
                        };
                    }
                    break;
                case PmxObject.Morph.Type.ImpulseMorph:
                    {
                        const indices = new Int32Array(morphOffsetCount);
                        const isLocals = new Array(morphOffsetCount);
                        const velocities = new Float32Array(morphOffsetCount * 3);
                        const torques = new Float32Array(morphOffsetCount * 3);
                        for (let i = 0; i < morphOffsetCount; ++i) {
                            indices[i] = indexReader.getRigidBodyIndex(dataDeserializer);
                            isLocals[i] = dataDeserializer.getUint8() === 1;
                            velocities[i * 3 + 0] = dataDeserializer.getFloat32();
                            velocities[i * 3 + 1] = dataDeserializer.getFloat32();
                            velocities[i * 3 + 2] = dataDeserializer.getFloat32();
                            torques[i * 3 + 0] = dataDeserializer.getFloat32();
                            torques[i * 3 + 1] = dataDeserializer.getFloat32();
                            torques[i * 3 + 2] = dataDeserializer.getFloat32();
                        }
                        morph = {
                            ...morph,
                            indices,
                            isLocals,
                            velocities,
                            torques
                        };
                    }
                    break;
                default:
                    throw new Error(`Unknown morph type: ${type}`);
            }
            morphs.push(morph);
        }
        return morphs;
    }
    static _ParseDisplayFrames(dataDeserializer, indexReader) {
        const displayFramesCount = dataDeserializer.getInt32();
        const displayFrames = [];
        for (let i = 0; i < displayFramesCount; ++i) {
            const name = dataDeserializer.getDecoderString(dataDeserializer.getInt32(), false);
            const englishName = dataDeserializer.getDecoderString(dataDeserializer.getInt32(), false);
            const isSpecialFrame = dataDeserializer.getUint8() === 1;
            const elementsCount = dataDeserializer.getInt32();
            const frames = [];
            for (let i = 0; i < elementsCount; ++i) {
                const frameType = dataDeserializer.getUint8();
                const frameIndex = frameType === PmxObject.DisplayFrame.FrameData.FrameType.Bone
                    ? indexReader.getBoneIndex(dataDeserializer)
                    : indexReader.getMorphIndex(dataDeserializer);
                const frame = {
                    type: frameType,
                    index: frameIndex
                };
                frames.push(frame);
            }
            const displayFrame = {
                name,
                englishName,
                isSpecialFrame,
                frames
            };
            displayFrames.push(displayFrame);
        }
        return displayFrames;
    }
    static _ParseRigidBodies(dataDeserializer, indexReader) {
        const rigidBodiesCount = dataDeserializer.getInt32();
        const rigidBodies = [];
        for (let i = 0; i < rigidBodiesCount; ++i) {
            const name = dataDeserializer.getDecoderString(dataDeserializer.getInt32(), false);
            const englishName = dataDeserializer.getDecoderString(dataDeserializer.getInt32(), false);
            const boneIndex = indexReader.getBoneIndex(dataDeserializer);
            const collisionGroup = dataDeserializer.getUint8();
            const collisionMask = dataDeserializer.getUint16();
            const shapeType = dataDeserializer.getUint8();
            const shapeSize = dataDeserializer.getFloat32Tuple(3);
            const shapePosition = dataDeserializer.getFloat32Tuple(3);
            const shapeRotation = dataDeserializer.getFloat32Tuple(3);
            const mass = dataDeserializer.getFloat32();
            const linearDamping = dataDeserializer.getFloat32();
            const angularDamping = dataDeserializer.getFloat32();
            const repulsion = dataDeserializer.getFloat32();
            const friction = dataDeserializer.getFloat32();
            const physicsMode = dataDeserializer.getUint8();
            const rigidBody = {
                name,
                englishName,
                boneIndex,
                collisionGroup,
                collisionMask,
                shapeType,
                shapeSize,
                shapePosition,
                shapeRotation,
                mass,
                linearDamping,
                angularDamping,
                repulsion,
                friction,
                physicsMode
            };
            rigidBodies.push(rigidBody);
        }
        return rigidBodies;
    }
    static _ParseJoints(dataDeserializer, indexReader) {
        const jointsCount = dataDeserializer.getInt32();
        const joints = [];
        for (let i = 0; i < jointsCount; ++i) {
            const name = dataDeserializer.getDecoderString(dataDeserializer.getInt32(), false);
            const englishName = dataDeserializer.getDecoderString(dataDeserializer.getInt32(), false);
            const type = dataDeserializer.getUint8();
            const rigidbodyIndexA = indexReader.getRigidBodyIndex(dataDeserializer);
            const rigidbodyIndexB = indexReader.getRigidBodyIndex(dataDeserializer);
            const position = dataDeserializer.getFloat32Tuple(3);
            const rotation = dataDeserializer.getFloat32Tuple(3);
            const positionMin = dataDeserializer.getFloat32Tuple(3);
            const positionMax = dataDeserializer.getFloat32Tuple(3);
            const rotationMin = dataDeserializer.getFloat32Tuple(3);
            const rotationMax = dataDeserializer.getFloat32Tuple(3);
            const springPosition = dataDeserializer.getFloat32Tuple(3);
            const springRotation = dataDeserializer.getFloat32Tuple(3);
            const joint = {
                name,
                englishName,
                type,
                rigidbodyIndexA,
                rigidbodyIndexB,
                position,
                rotation,
                positionMin,
                positionMax,
                rotationMin,
                rotationMax,
                springPosition,
                springRotation
            };
            joints.push(joint);
        }
        return joints;
    }
    static _ParseSoftBodies(dataDeserializer, indexReader, header) {
        const softBodiesCount = dataDeserializer.getInt32();
        const softBodies = [];
        for (let i = 0; i < softBodiesCount; ++i) {
            const name = dataDeserializer.getDecoderString(dataDeserializer.getInt32(), false);
            const englishName = dataDeserializer.getDecoderString(dataDeserializer.getInt32(), false);
            const type = dataDeserializer.getUint8();
            const materialIndex = indexReader.getMaterialIndex(dataDeserializer);
            const collisionGroup = dataDeserializer.getUint8();
            const collisionMask = dataDeserializer.getUint16();
            const flags = dataDeserializer.getUint8();
            const bLinkDistance = dataDeserializer.getInt32();
            const clusterCount = dataDeserializer.getInt32();
            const totalMass = dataDeserializer.getFloat32();
            const collisionMargin = dataDeserializer.getFloat32();
            const aeroModel = dataDeserializer.getInt32();
            const config = {
                vcf: dataDeserializer.getFloat32(),
                dp: dataDeserializer.getFloat32(),
                dg: dataDeserializer.getFloat32(),
                lf: dataDeserializer.getFloat32(),
                pr: dataDeserializer.getFloat32(),
                vc: dataDeserializer.getFloat32(),
                df: dataDeserializer.getFloat32(),
                mt: dataDeserializer.getFloat32(),
                chr: dataDeserializer.getFloat32(),
                khr: dataDeserializer.getFloat32(),
                shr: dataDeserializer.getFloat32(),
                ahr: dataDeserializer.getFloat32()
            };
            const cluster = {
                srhrCl: dataDeserializer.getFloat32(),
                skhrCl: dataDeserializer.getFloat32(),
                sshrCl: dataDeserializer.getFloat32(),
                srSpltCl: dataDeserializer.getFloat32(),
                skSpltCl: dataDeserializer.getFloat32(),
                ssSpltCl: dataDeserializer.getFloat32()
            };
            const iteration = {
                vIt: dataDeserializer.getInt32(),
                pIt: dataDeserializer.getInt32(),
                dIt: dataDeserializer.getInt32(),
                cIt: dataDeserializer.getInt32()
            };
            const material = {
                lst: dataDeserializer.getInt32(),
                ast: dataDeserializer.getInt32(),
                vst: dataDeserializer.getInt32()
            };
            const anchorsCount = dataDeserializer.getInt32();
            const anchors = [];
            for (let j = 0; j < anchorsCount; ++j) {
                const rigidbodyIndex = indexReader.getRigidBodyIndex(dataDeserializer);
                const vertexIndex = indexReader.getVertexIndex(dataDeserializer);
                const isNearMode = dataDeserializer.getUint8() !== 0;
                const anchorRigidBody = {
                    rigidbodyIndex,
                    vertexIndex,
                    isNearMode
                };
                anchors.push(anchorRigidBody);
            }
            const vertexPinCount = dataDeserializer.getInt32();
            const vertexPinArrayBuffer = new ArrayBuffer(vertexPinCount * header.vertexIndexSize);
            let vertexPins;
            switch (header.vertexIndexSize) {
                case 1:
                    vertexPins = new Uint8Array(vertexPinArrayBuffer);
                    break;
                case 2:
                    vertexPins = new Uint16Array(vertexPinArrayBuffer);
                    break;
                case 4:
                    vertexPins = new Int32Array(vertexPinArrayBuffer);
                    break;
                default:
                    throw new Error(`Invalid vertexIndexSize: ${header.vertexIndexSize}`);
            }
            for (let i = 0; i < vertexPinCount; ++i) {
                vertexPins[i] = indexReader.getVertexIndex(dataDeserializer);
            }
            const softBody = {
                name,
                englishName,
                type,
                materialIndex,
                collisionGroup,
                collisionMask,
                flags,
                bLinkDistance,
                clusterCount,
                totalMass,
                collisionMargin,
                aeroModel,
                config,
                cluster,
                iteration,
                material,
                anchors,
                vertexPins
            };
            softBodies.push(softBody);
        }
        return softBodies;
    }
}
